import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, MessageSquare } from "lucide-react";
import { useLang } from "../../i18n/LanguageContext";
import { PentominoMark } from "./PentominoMark";

const ease = [0.22, 1, 0.36, 1];

/**
 * Shared inner-page hero (dark tinta section with PentominoMark on right).
 * Keeps the visual language consistent with Home's hero but shorter.
 */
export const PageHero = ({
    eyebrow,
    audienceTag,
    title,
    titleAccent,
    lead,
    piecePattern,
    showMark = true,
    testId = "page-hero",
}) => {
    const { t, waUrl } = useLang();

    return (
        <section
            className="on-dark relative overflow-hidden bg-tinta pt-grain text-white"
            data-testid={testId}
        >
            {/* subtle angled divider */}
            <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent to-tinta-2/40" />

            <div className="wrap relative z-10 grid grid-cols-1 items-center gap-14 py-20 lg:min-h-[480px] lg:grid-cols-[1.3fr_0.7fr] lg:py-28">
                <div>
                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, ease, delay: 0.05 }}
                    >
                        {audienceTag ? (
                            <div className="mb-5 flex items-center gap-3">
                                {piecePattern && (
                                    <span
                                        className="piece-marker"
                                        aria-hidden="true"
                                    >
                                        {piecePattern.flat().map((cls, idx) => (
                                            <i key={idx} className={cls} />
                                        ))}
                                    </span>
                                )}
                                <span className="text-xs font-semibold uppercase tracking-widest text-[#C5CDD8]">
                                    {audienceTag}
                                </span>
                            </div>
                        ) : (
                            <div className="pt-eyebrow mb-5">
                                <span className="dot" />
                                <span>{eyebrow}</span>
                            </div>
                        )}
                    </motion.div>

                    <motion.h1
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.65, ease, delay: 0.15 }}
                        className="h1 max-w-[22ch] text-white"
                        style={{
                            fontSize: "clamp(2.1rem, 4.4vw, 3.6rem)",
                        }}
                        data-testid={`${testId}-title`}
                    >
                        {title}{" "}
                        {titleAccent && (
                            <span className="text-coral">{titleAccent}</span>
                        )}
                    </motion.h1>

                    {lead && (
                        <motion.p
                            initial={{ opacity: 0, y: 18 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.65, ease, delay: 0.3 }}
                            className="lead mt-5 max-w-[58ch] text-[#C5CDD8]"
                            data-testid={`${testId}-lead`}
                        >
                            {lead}
                        </motion.p>
                    )}

                    <motion.div
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.65, ease, delay: 0.45 }}
                        className="mt-8 flex flex-wrap gap-3"
                    >
                        <a
                            href={waUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="pt-btn"
                            data-testid={`${testId}-cta-wa`}
                        >
                            <MessageSquare size={18} />
                            {t.common.ctaWhatsapp}
                        </a>
                        <Link
                            to="/contacto"
                            className="pt-btn alt"
                            data-testid={`${testId}-cta-contact`}
                        >
                            {t.common.ctaContact}
                            <ArrowRight size={16} />
                        </Link>
                    </motion.div>
                </div>

                {showMark && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.92 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.9, ease, delay: 0.2 }}
                        className="flex justify-center lg:justify-end"
                    >
                        <PentominoMark size={280} />
                    </motion.div>
                )}
            </div>
        </section>
    );
};
